import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, query, orderBy, limit, onSnapshot, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import AdBanner from '../components/AdBanner';
import { useSocial } from '../context/SocialContext';
import { IoBook, IoHeart, IoChatbubble, IoPersonAdd, IoStar, IoPeople, IoHeartOutline, IoChatbubbleOutline, IoShareOutline, IoSend } from 'react-icons/io5';
import './Feed.css';

const ACTIVITY_ICONS = {
  new_book: { icon: IoBook, color: 'var(--plum)', bg: 'rgba(139,111,142,0.1)' },
  like: { icon: IoHeart, color: 'var(--rose)', bg: 'rgba(194,122,122,0.1)' },
  comment: { icon: IoChatbubble, color: 'var(--amber)', bg: 'rgba(200,149,108,0.1)' },
  follow: { icon: IoPersonAdd, color: 'var(--sky)', bg: 'rgba(122,154,181,0.1)' },
  rating: { icon: IoStar, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
};

export function usePostActivity() {
  const { user } = useUser();
  const postActivity = async (type, data) => {
    if (!user) return;
    await addDoc(collection(db, 'activities'), {
      type,
      userId: user.id,
      userName: user.name,
      ...data,
      createdAt: serverTimestamp(),
    });
  };
  return postActivity;
}

function timeAgo(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return 'maintenant';
  if (seconds < 3600) return Math.floor(seconds / 60) + ' min';
  if (seconds < 86400) return Math.floor(seconds / 3600) + 'h';
  if (seconds < 604800) return Math.floor(seconds / 86400) + 'j';
  return date.toLocaleDateString('fr-FR');
}

export default function Feed() {
  const { user } = useUser();
  const { following } = useSocial();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState('publications');
  const [expandedPost, setExpandedPost] = useState(null);
  const [postComment, setPostComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [postComments, setPostComments] = useState({});

  // Charger les activités (connecté uniquement)
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'activities'), orderBy('createdAt', 'desc'), limit(50));
    const unsub = onSnapshot(q, (snapshot) => {
      setActivities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, [user]);

  // Charger les publications (visible sans connexion)
  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'), limit(100));
    const unsub = onSnapshot(q, (snapshot) => {
      setPosts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  // Charger les commentaires d'un post
  useEffect(() => {
    if (!expandedPost) return;
    const q = query(
      collection(db, 'posts', expandedPost, 'comments'),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      setPostComments(prev => ({
        ...prev,
        [expandedPost]: snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
      }));
    });
    return () => unsub();
  }, [expandedPost]);

  // Liker un post
  const handleLikePost = async (postId, currentLikes, likedBy) => {
    if (!user) { navigate('/auth'); return; }
    const alreadyLiked = likedBy && likedBy.includes(user.id);
    const postRef = doc(db, 'posts', postId);
    if (alreadyLiked) {
      await updateDoc(postRef, {
        likes: (currentLikes || 1) - 1,
        likedBy: (likedBy || []).filter(id => id !== user.id),
      });
    } else {
      await updateDoc(postRef, {
        likes: (currentLikes || 0) + 1,
        likedBy: [...(likedBy || []), user.id],
      });
    }
  };

  // Poster un commentaire
  const handlePostComment = async (postId) => {
    if (!user) { navigate('/auth'); return; }
    if (!postComment.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      await addDoc(collection(db, 'posts', postId, 'comments'), {
        text: postComment.trim(),
        userId: user.id,
        userName: user.name,
        userAvatar: user.avatarImage || null,
        userAvatarColor: user.avatarColor || 'var(--amber)',
        createdAt: serverTimestamp(),
      });
      const post = posts.find(p => p.id === postId);
      await updateDoc(doc(db, 'posts', postId), { comments: (post?.comments || 0) + 1 });
      setPostComment('');
    } catch (e) {
      console.error('Erreur commentaire:', e);
    }
    setSendingComment(false);
  };

  // Filtrer selon l'onglet
  const filteredPosts = filter === 'following'
    ? posts.filter(p => user && (following.includes(p.authorId) || p.authorId === user.id))
    : posts;

  const filteredActivities = filter === 'following'
    ? activities.filter(a => user && (following.includes(a.userId) || a.userId === user.id))
    : activities;

  const getActivityText = (activity) => {
    switch (activity.type) {
      case 'new_book': return <span>a publié <strong>{activity.bookTitle}</strong></span>;
      case 'like': return <span>a aimé <strong>{activity.bookTitle}</strong></span>;
      case 'comment': return <span>a commenté <strong>{activity.bookTitle}</strong></span>;
      case 'follow': return <span>a commencé à suivre <strong>{activity.targetName}</strong></span>;
      case 'rating': return <span>a noté <strong>{activity.bookTitle}</strong> {'⭐'.repeat(activity.rating)}</span>;
      default: return <span>a fait quelque chose</span>;
    }
  };

  return (
    <div className="page feed-page">
      <div className="feed-header">
        <h1 className="feed-title">Fil d'actualité</h1>
        <p className="feed-subtitle">Quoi de neuf sur Plumly</p>
      </div>

      <div className="feed-filters">
        <motion.button
          className={'feed-filter ' + (filter === 'all' ? 'feed-filter-active' : '')}
          onClick={() => setFilter('all')}
          whileTap={{ scale: 0.95 }}
        >
          🌍 Tout le monde
        </motion.button>
        <motion.button
          className={'feed-filter ' + (filter === 'following' ? 'feed-filter-active' : '')}
          onClick={() => { if (!user) { navigate('/auth'); return; } setFilter('following'); }}
          whileTap={{ scale: 0.95 }}
        >
          <IoPeople /> Abonnements
        </motion.button>
      </div>

      <div className="feed-tabs">
        <button className={'feed-tab ' + (tab === 'publications' ? 'feed-tab-active' : '')} onClick={() => setTab('publications')}>
          📝 Publications
        </button>
        <button className={'feed-tab ' + (tab === 'activites' ? 'feed-tab-active' : '')} onClick={() => setTab('activites')}>
          ⚡ Activités
        </button>
      </div>

      <AdBanner slot="feed-top" format="horizontal" />

      {/* TAB PUBLICATIONS */}
      {tab === 'publications' && (
        <div className="feed-posts">
          {filteredPosts.length > 0 ? (
            filteredPosts.map((post, i) => {
              const isLiked = user && post.likedBy && post.likedBy.includes(user.id);
              return (
                <React.Fragment key={post.id}>
                  {i > 0 && i % 5 === 0 && <AdBanner slot="feed-inline" format="small" />}
                  <motion.div
                    className="feed-post"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                  >
                    <div className="feed-post-header">
                      <div
                        className="feed-post-avatar"
                        style={{
                          background: post.authorAvatar
                            ? `url(${post.authorAvatar}) center/cover no-repeat`
                            : post.authorAvatarColor || 'var(--amber)'
                        }}
                        onClick={() => navigate('/author/author_' + post.authorId)}
                      >
                        {!post.authorAvatar && (post.authorName || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="feed-post-meta">
                        <strong
                          className="feed-post-author-name"
                          onClick={() => navigate('/author/author_' + post.authorId)}
                        >
                          {post.authorName}
                        </strong>
                        <span className="feed-post-time">{timeAgo(post.createdAt)}</span>
                      </div>
                    </div>

                    <p className="feed-post-text">{post.content}</p>

                    <div className="feed-post-actions">
                      <motion.button
                        className={'feed-post-action ' + (isLiked ? 'feed-post-action-liked' : '')}
                        onClick={() => handleLikePost(post.id, post.likes, post.likedBy)}
                        whileTap={{ scale: 0.9 }}
                      >
                        {isLiked ? <IoHeart /> : <IoHeartOutline />}
                        {(post.likes || 0) > 0 && <span>{post.likes}</span>}
                      </motion.button>
                      <button
                        className="feed-post-action"
                        onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
                      >
                        <IoChatbubbleOutline />
                        {(post.comments || 0) > 0 && <span>{post.comments}</span>}
                      </button>
                      <button
                        className="feed-post-action"
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.origin + '/author/author_' + post.authorId);
                          alert('Lien copié !');
                        }}
                      >
                        <IoShareOutline />
                      </button>
                    </div>

                    {/* Commentaires */}
                    {expandedPost === post.id && (
                      <motion.div
                        className="feed-post-comments"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {user ? (
                          <div className="feed-comment-input">
                            <div className="feed-comment-avatar" style={{
                              background: user.avatarImage
                                ? `url(${user.avatarImage}) center/cover no-repeat`
                                : user.avatarColor || 'var(--amber)'
                            }}>
                              {!user.avatarImage && (user.name || '?').charAt(0).toUpperCase()}
                            </div>
                            <input
                              className="feed-comment-field"
                              placeholder="Écrire un commentaire..."
                              value={postComment}
                              onChange={e => setPostComment(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handlePostComment(post.id)}
                              maxLength={300}
                            />
                            <motion.button
                              className="feed-comment-send"
                              onClick={() => handlePostComment(post.id)}
                              disabled={!postComment.trim() || sendingComment}
                              whileTap={{ scale: 0.9 }}
                            >
                              <IoSend />
                            </motion.button>
                          </div>
                        ) : (
                          <p className="feed-login-prompt" onClick={() => navigate('/auth')}>
                            <strong>Connecte-toi</strong> pour commenter
                          </p>
                        )}

                        <div className="feed-comments-list">
                          {(postComments[post.id] || []).map(comment => (
                            <div key={comment.id} className="feed-comment">
                              <div
                                className="feed-comment-user-avatar feed-comment-clickable"
                                style={{
                                  background: comment.userAvatar
                                    ? `url(${comment.userAvatar}) center/cover no-repeat`
                                    : comment.userAvatarColor || 'var(--amber)'
                                }}
                                onClick={() => navigate('/author/author_' + comment.userId)}
                              >
                                {!comment.userAvatar && (comment.userName || '?').charAt(0).toUpperCase()}
                              </div>
                              <div className="feed-comment-body">
                                <div className="feed-comment-bubble">
                                  <strong
                                    className="feed-comment-name feed-comment-clickable"
                                    onClick={() => navigate('/author/author_' + comment.userId)}
                                  >
                                    {comment.userName}
                                  </strong>
                                  <p className="feed-comment-text">{comment.text}</p>
                                </div>
                                <span className="feed-comment-time">{timeAgo(comment.createdAt)}</span>
                              </div>
                            </div>
                          ))}
                          {(postComments[post.id] || []).length === 0 && (
                            <p className="feed-no-comments">Aucun commentaire. Sois le premier !</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                </React.Fragment>
              );
            })
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📝</div>
              <p className="empty-state-text">
                {filter === 'following'
                  ? "Tes abonnements n'ont pas encore publié"
                  : 'Aucune publication pour le moment'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* TAB ACTIVITÉS */}
      {tab === 'activites' && (
        <div className="feed-list">
          {filteredActivities.length > 0 ? (
            filteredActivities.map((activity, i) => {
              const activityStyle = ACTIVITY_ICONS[activity.type] || ACTIVITY_ICONS.like;
              const Icon = activityStyle.icon;
              return (
                <React.Fragment key={activity.id}>
                  {i > 0 && i % 5 === 0 && <AdBanner slot="feed-inline" format="small" />}
                  <motion.div
                    className="feed-item"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    onClick={() => { if (activity.bookId) navigate('/book/' + activity.bookId); }}
                    style={{ cursor: activity.bookId ? 'pointer' : 'default' }}
                  >
                    <div className="feed-item-icon" style={{ background: activityStyle.bg, color: activityStyle.color }}>
                      <Icon />
                    </div>
                    <div className="feed-item-content">
                      <p className="feed-item-text">
                        <strong
                          className="feed-item-author"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activity.userId) navigate('/author/author_' + activity.userId);
                          }}
                        >
                          {activity.userName}
                        </strong>
                        {' '}{getActivityText(activity)}
                      </p>
                      <span className="feed-item-time">{timeAgo(activity.createdAt)}</span>
                    </div>
                  </motion.div>
                </React.Fragment>
              );
            })
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">⚡</div>
              <p className="empty-state-text">
                {filter === 'following'
                  ? "Tes abonnements n'ont pas encore d'activité"
                  : 'Aucune activité pour le moment'}
              </p>
            </div>
          )}
        </div>
      )}

      <AdBanner slot="feed-bottom" format="horizontal" />
    </div>
  );
}